import { useState, useEffect, useMemo } from 'react';
import {
  OnboardingStepProps,
  BarDefinition,
  Principle,
  Competency,
  EmphasisLevel,
  DEFAULT_PRINCIPLES,
  ROLE_COMPETENCY_SUGGESTIONS,
} from './types';

const EMPHASIS_OPTIONS: { id: EmphasisLevel; label: string }[] = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Med' },
  { id: 'high', label: 'High' },
];

function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

function detectRoleCategory(title: string): string {
  const lower = title.toLowerCase();
  if (lower.includes('engineer') || lower.includes('developer') || lower.includes('swe') || lower.includes('backend') || lower.includes('frontend') || lower.includes('fullstack')) {
    return 'engineer';
  }
  if (lower.includes('design') || lower.includes('ux') || lower.includes('ui')) {
    return 'designer';
  }
  if (lower.includes('product') || lower.includes('pm')) {
    return 'product';
  }
  if (lower.includes('data') || lower.includes('analyst') || lower.includes('science')) {
    return 'data';
  }
  return 'default';
}

export function StepBar({ state, onUpdate, onNext, onBack }: OnboardingStepProps) {
  const roleTitle = state.role?.title ?? '';
  const roleCategory = useMemo(() => detectRoleCategory(roleTitle), [roleTitle]);

  const defaultPrinciples: Principle[] = DEFAULT_PRINCIPLES.map((p) => ({
    ...p,
    id: generateId(),
  }));

  const suggestedCompetencies: Competency[] = useMemo(() => {
    const suggestions = ROLE_COMPETENCY_SUGGESTIONS[roleCategory] || ROLE_COMPETENCY_SUGGESTIONS.default;
    return suggestions.map((name) => ({
      id: generateId(),
      name,
      emphasis: 'medium' as EmphasisLevel,
      enabled: true,
    }));
  }, [roleCategory]);

  const [principles, setPrinciples] = useState<Principle[]>(
    state.bar?.principles ?? defaultPrinciples
  );
  const [competencies, setCompetencies] = useState<Competency[]>(
    state.bar?.competencies ?? suggestedCompetencies
  );

  useEffect(() => {
    const bar: BarDefinition = {
      principles: principles.filter((p) => p.enabled),
      competencies: competencies.filter((c) => c.enabled),
    };
    onUpdate({ bar });
  }, [principles, competencies]);

  const togglePrinciple = (id: string) => {
    setPrinciples((prev) =>
      prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p))
    );
  };

  const setPrincipleEmphasis = (id: string, emphasis: EmphasisLevel) => {
    setPrinciples((prev) =>
      prev.map((p) => (p.id === id ? { ...p, emphasis } : p))
    );
  };

  const toggleCompetency = (id: string) => {
    setCompetencies((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c))
    );
  };

  const setCompetencyEmphasis = (id: string, emphasis: EmphasisLevel) => {
    setCompetencies((prev) =>
      prev.map((c) => (c.id === id ? { ...c, emphasis } : c))
    );
  };

  const hasSelection = principles.some((p) => p.enabled) || competencies.some((c) => c.enabled);

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-surface-900 mb-4">
          What matters for this role?
        </h1>
        <p className="text-lg text-surface-500 max-w-xl mx-auto">
          Set your bar. Toggle what to evaluate and how heavily it should weigh.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-10">
        {/* Company Principles */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-surface-900">Company Principles</h2>
          </div>
          <p className="text-sm text-surface-500 mb-4">
            Universal values you evaluate every candidate on.
          </p>
          <div className="space-y-2">
            {principles.map((principle) => (
              <div
                key={principle.id}
                className={`
                  flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200
                  ${principle.enabled
                    ? 'border-brand-200 bg-brand-50/50'
                    : 'border-surface-200 bg-surface-50 opacity-60'
                  }
                `}
              >
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={principle.enabled}
                    onChange={() => togglePrinciple(principle.id)}
                    className="w-5 h-5 rounded border-2 border-surface-300 text-brand-600 focus:ring-brand-500 focus:ring-offset-0 cursor-pointer"
                  />
                  <span className={`font-medium ${principle.enabled ? 'text-surface-900' : 'text-surface-500'}`}>
                    {principle.name}
                  </span>
                </label>
                {principle.enabled && (
                  <div className="flex gap-1">
                    {EMPHASIS_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setPrincipleEmphasis(principle.id, opt.id)}
                        className={`
                          px-3 py-1 rounded-md text-xs font-medium transition-colors
                          ${principle.emphasis === opt.id
                            ? 'bg-brand-600 text-white'
                            : 'bg-surface-200 text-surface-600 hover:bg-surface-300'
                          }
                        `}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Role Competencies */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-cyan-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-surface-900">Role Competencies</h2>
          </div>
          <p className="text-sm text-surface-500 mb-4">
            Skills specific to <span className="font-medium text-surface-700">{roleTitle || 'this role'}</span>.
          </p>
          <div className="space-y-2">
            {competencies.map((competency) => (
              <div
                key={competency.id}
                className={`
                  flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200
                  ${competency.enabled
                    ? 'border-cyan-200 bg-cyan-50/50'
                    : 'border-surface-200 bg-surface-50 opacity-60'
                  }
                `}
              >
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={competency.enabled}
                    onChange={() => toggleCompetency(competency.id)}
                    className="w-5 h-5 rounded border-2 border-surface-300 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                  />
                  <span className={`font-medium ${competency.enabled ? 'text-surface-900' : 'text-surface-500'}`}>
                    {competency.name}
                  </span>
                </label>
                {competency.enabled && (
                  <div className="flex gap-1">
                    {EMPHASIS_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setCompetencyEmphasis(competency.id, opt.id)}
                        className={`
                          px-3 py-1 rounded-md text-xs font-medium transition-colors
                          ${competency.emphasis === opt.id
                            ? 'bg-cyan-600 text-white'
                            : 'bg-surface-200 text-surface-600 hover:bg-surface-300'
                          }
                        `}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-12 flex justify-center gap-4">
        <button onClick={onBack} className="btn btn-secondary text-base px-8 py-3.5">
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!hasSelection}
          className={`
            btn text-base px-10 py-3.5 transition-all duration-200
            ${hasSelection
              ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-500/25'
              : 'bg-surface-200 text-surface-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

